import { useState } from 'react';
import { CheckCircle2, XCircle, ArrowLeft, Download, Loader2, Info, HelpCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { BalanceIcon } from './icons/BalanceIcon';
import { formatBalance } from '../../utils/balanceFormatter';
import { invoiceService } from '../../services/invoiceService';
import { toast } from 'sonner';
import { SupportModal } from './SupportModal';

interface RechargeStatusPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: 'success' | 'failed' | 'pending';
  amount: number;
  transactionId?: string;
  walletTransactionId?: string;
  timestamp?: string;
  paymentMethod?: string;
  newBalance?: number;
}

export function RechargeStatusPage({
  open,
  onOpenChange,
  status,
  amount,
  transactionId,
  walletTransactionId,
  paymentMethod,
  newBalance,
}: RechargeStatusPageProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const timestamp = new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  if (!open) return null;

  const showAmount = !(status === 'pending' && !(amount > 0));

  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-[2000] overflow-auto transition-colors">
      {/* Header */}
      {/* This page is `fixed inset-0`, so it escapes ProtectedLayout's
          pt-[env(safe-area-inset-top)] and must handle the inset itself —
          without it the back button and title render under the notch /
          Dynamic Island on iOS. */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 pb-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {status === 'success' ? 'Balance Added!' : status === 'pending' ? 'Confirming payment' : 'Addition Failed'}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-[max(6rem,env(safe-area-inset-bottom))]">
        {/* Status Icon */}
        <div className="flex flex-col items-center justify-center py-8 mb-6">
          {status === 'success' ? (
            <>
              <div className="relative mb-6">
                <div className="w-32 h-32 bg-green-50 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-20 h-20 text-green-600 dark:text-green-400" />
                </div>
                <div className="absolute inset-0 w-32 h-32 bg-green-200 dark:bg-green-600 rounded-full animate-ping opacity-20"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Balance Added Successfully!</h2>
              <p className="text-gray-500 dark:text-gray-400 text-center">Balance has been added to your wallet</p>
            </>
          ) : status === 'pending' ? (
            <>
              <div className="relative mb-6">
                <div className="w-32 h-32 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <Clock className="w-20 h-20 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Confirming your payment</h2>
              <p className="text-gray-500 dark:text-gray-400 text-center">
                You&apos;ll get a notification when the balance lands. Don&apos;t pay again.
              </p>
            </>
          ) : (
            <>
              <div className="relative mb-6">
                <div className="w-32 h-32 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <XCircle className="w-20 h-20 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Addition Failed</h2>
              <p className="text-gray-500 dark:text-gray-400 text-center">Something went wrong with your payment</p>
            </>
          )}
        </div>

        {showAmount && (
        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 rounded-3xl p-6 mb-6 border-2 border-gray-100 dark:border-gray-700">
          <div className="text-center mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {status === 'pending' ? 'Amount' : 'Amount Added'}
            </p>
            <p className="text-5xl font-bold text-gray-900 dark:text-white flex items-center justify-center gap-2">
              <BalanceIcon className="w-10 h-10" /> {formatBalance(amount)}
            </p>
          </div>

          {status === 'success' && newBalance !== undefined && (
            <div className="flex items-center justify-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <BalanceIcon className="w-5 h-5 text-[#8bc43e]" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                New Balance: <span className="font-bold text-[#8bc43e]"><BalanceIcon className="w-4 h-4 inline" /> {formatBalance(newBalance)}</span>
              </p>
            </div>
          )}
        </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mb-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Transaction Details</h3>

          <div className="space-y-4">
            {transactionId && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400 shrink-0">Transaction ID</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white font-mono text-right break-all">{transactionId}</span>
            </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Date & Time</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{timestamp}</span>
            </div>

            {paymentMethod && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Payment Method</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{paymentMethod}</span>
            </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
              <span className={`text-sm font-semibold ${
                status === 'success'
                  ? 'text-green-600 dark:text-green-400'
                  : status === 'pending'
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400'
              }`}>
                {status === 'success' ? 'Success' : status === 'pending' ? 'Pending' : 'Failed'}
              </span>
            </div>

            {status === 'success' && newBalance !== undefined && (
              <>
                <div className="h-px bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Previous Balance</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1"><BalanceIcon className="w-3.5 h-3.5" /> {formatBalance(newBalance - amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Amount Added</span>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center gap-1">+<BalanceIcon className="w-3.5 h-3.5" /> {formatBalance(amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Current Balance</span>
                  <span className="text-sm font-bold text-[#8bc43e] flex items-center gap-1"><BalanceIcon className="w-3.5 h-3.5" /> {formatBalance(newBalance)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Failure Reason (if failed) */}
        {status === 'failed' && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-5 mb-6">
            <h3 className="font-semibold text-red-900 dark:text-red-400 mb-2">Reason for Failure</h3>
            <p className="text-sm text-red-700 dark:text-red-300">
              Payment could not be processed. This could be due to insufficient funds, network issues, or payment gateway timeout.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {status === 'success' ? (
            <>
              <Button
                onClick={() => onOpenChange(false)}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:shadow-lg text-white font-bold"
              >
                Back to Wallet
              </Button>

              {walletTransactionId && (
                <Button
                  onClick={async () => {
                    setIsDownloading(true);
                    try {
                      const invoice = await invoiceService.getOrGenerateInvoice(walletTransactionId);
                      if (invoice.pdfUrl) {
                        window.open(invoice.pdfUrl, '_blank');
                      } else {
                        toast.error('Receipt URL not available');
                      }
                    } catch (err: any) {
                      const msg = err.response?.data?.message || 'Failed to download receipt';
                      toast.error(msg);
                    } finally {
                      setIsDownloading(false);
                    }
                  }}
                  disabled={isDownloading}
                  className="w-full h-14 rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-bold"
                >
                  {isDownloading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  {isDownloading ? 'Generating Receipt...' : 'Download Receipt'}
                </Button>
              )}
            </>
          ) : status === 'pending' ? (
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:shadow-lg text-white font-bold"
            >
              Back to Wallet
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  onOpenChange(false);
                }}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:shadow-lg text-white font-bold"
              >
                Try Again
              </Button>

              <Button
                onClick={() => onOpenChange(false)}
                className="w-full h-14 rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-bold"
              >
                Back to Wallet
              </Button>
            </>
          )}
        </div>

        {/* Payment Disclosure */}
        <div className="mt-6 bg-blue-50/60 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60 rounded-2xl p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800/40 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Payment Information</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">What this payment covers</p>
            </div>
          </div>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            <p>
              Your balance is used exclusively for EV charging services on the EVeeBuddy network.
            </p>
            <p>
              Added balance is <span className="font-semibold">non-refundable once a charging session has started</span>. For unused balance refund requests or payment disputes, please contact support.
            </p>
          </div>
          <button
            onClick={() => setSupportOpen(true)}
            className="mt-4 w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800/60 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            Contact Support for Payment Issues
          </button>
        </div>
      </div>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}
